// Small labels. Three flavors:
// - Badge: semantic tones (status, counts, "Read only") from the token palette.
// - Chip: the iOS Chip (Theme.swift) for any user-chosen hex (stage, priority, record type, tag): the color at 13%
//   (22% dark) behind text in a readable shade of the same color, or a solid fill. Optionally a toggle (filter chips)
//   or removable (tag editors).
// - ColorChip: a neutral pill with a colored dot, for dense tables where a dozen tinted chips would be noise
//   (territory, team, owner columns). Neither knows about record types or stages; callers pass the hex.

import { X } from 'lucide-react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { cn } from './cn';
import { normalizeHex, tintStyle } from './color';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'navy' | 'purple';
export type BadgeVariant = 'soft' | 'solid' | 'outline';
export type ChipSize = 'xs' | 'sm' | 'md';

const SIZE: Record<ChipSize, string> = {
  xs: 'h-[18px] px-1.5 text-[10px] gap-1 [&_svg]:size-2.5',
  sm: 'h-[22px] px-2 text-[11px] gap-1 [&_svg]:size-3',
  md: 'h-6 px-2.5 text-xs gap-1.5 [&_svg]:size-3.5',
};

const SOFT: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-fg-muted',
  primary: 'bg-primary-soft text-primary-text',
  success: 'bg-success-soft text-success-text',
  warning: 'bg-warning-soft text-warning-text',
  danger: 'bg-danger-soft text-danger-text',
  info: 'bg-info-soft text-info-text',
  navy: 'bg-[rgb(16_42_94/0.1)] text-navy dark:bg-[rgb(163_189_247/0.16)] dark:text-accent',
  purple: 'bg-[rgb(125_77_191/0.13)] text-[#6a3fae] dark:bg-[rgb(125_77_191/0.25)] dark:text-[#c3a6ef]',
};

const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-grey text-white',
  primary: 'gt-primary-fill text-white',
  success: 'bg-success text-white',
  warning: 'bg-warning text-navy',
  danger: 'gt-danger-fill text-white',
  info: 'bg-info text-white',
  navy: 'bg-navy text-white',
  purple: 'bg-purple text-white',
};

const OUTLINE: Record<BadgeTone, string> = {
  neutral: 'border border-line-strong text-fg-muted',
  primary: 'border border-primary/50 text-primary-text',
  success: 'border border-success/50 text-success-text',
  warning: 'border border-warning/60 text-warning-text',
  danger: 'border border-danger/50 text-danger-text',
  info: 'border border-info/50 text-info-text',
  navy: 'border border-navy/40 text-navy dark:border-accent/50 dark:text-accent',
  purple: 'border border-purple/50 text-[#6a3fae] dark:text-[#c3a6ef]',
};

const PILL = 'inline-flex max-w-full shrink-0 items-center rounded-full font-semibold whitespace-nowrap leading-none';

export interface BadgeProps {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: ChipSize;
  icon?: ReactNode;
  /** Uppercase with tracking (iOS caps badges such as "NEW"). */
  caps?: boolean;
  title?: string;
  className?: string;
  children: ReactNode;
}

export function Badge({
  tone = 'neutral',
  variant = 'soft',
  size = 'sm',
  icon,
  caps,
  title,
  className,
  children,
}: BadgeProps) {
  const palette = variant === 'solid' ? SOLID : variant === 'outline' ? OUTLINE : SOFT;
  return (
    <span
      title={title}
      className={cn(PILL, SIZE[size], palette[tone], caps && 'tracking-[0.06em] uppercase', className)}
    >
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

export interface ChipProps {
  /** Any "#RRGGBB" (unparseable values render gray, like Theme.color(hex:)). Navy by default, as iOS. */
  color?: string;
  filled?: boolean;
  size?: ChipSize;
  icon?: ReactNode;
  title?: string;
  /** Makes the chip a toggle button (filter chips); `selected` then shows the filled look. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  selected?: boolean;
  /** Adds a remove (x) button with this accessible label, e.g. "Remove tag VIP". */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
  children: ReactNode;
}

export function Chip({
  color = '#102A5E',
  filled = false,
  size = 'sm',
  icon,
  title,
  onClick,
  selected,
  onRemove,
  removeLabel,
  className,
  children,
}: ChipProps) {
  const solid = filled || !!selected;
  const style: CSSProperties = tintStyle(color, solid);
  const body = (
    <>
      {icon}
      <span className="truncate">{children}</span>
    </>
  );
  const classes = cn(PILL, SIZE[size], className);
  if (onClick) {
    return (
      <button
        type="button"
        title={title}
        aria-pressed={selected ?? undefined}
        onClick={onClick}
        style={style}
        className={cn(classes, 'transition-[filter,transform] hover:brightness-[0.97] active:scale-[0.97]')}
      >
        {body}
      </button>
    );
  }
  return (
    <span title={title} style={style} className={cn(classes, onRemove && 'pr-0.5')}>
      {body}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? 'Remove'}
          className="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full opacity-70 hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/15"
        >
          <X aria-hidden className="size-2.5" strokeWidth={3} />
        </button>
      )}
    </span>
  );
}

export interface ColorChipProps {
  color?: string | null;
  size?: ChipSize;
  title?: string;
  className?: string;
  children: ReactNode;
}

/** Neutral pill with a colored dot: "● West Loop", "● Jane Doe". */
export function ColorChip({ color, size = 'sm', title, className, children }: ColorChipProps) {
  return (
    <span
      title={title}
      className={cn(PILL, SIZE[size], 'border border-line bg-surface-2 font-medium text-fg', className)}
    >
      <span
        aria-hidden
        className="size-2 shrink-0 rounded-full shadow-[inset_0_0_0_0.5px_rgb(0_0_0/0.15)]"
        style={{ backgroundColor: normalizeHex(color) }}
      />
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Just the dot (legends, select options rendered as custom rows). */
export function ColorDot({ color, size = 10, className }: { color?: string | null; size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block shrink-0 rounded-full shadow-[inset_0_0_0_0.5px_rgb(0_0_0/0.15)]', className)}
      style={{ width: size, height: size, backgroundColor: normalizeHex(color) }}
    />
  );
}
